import { useEffect, useRef, useState } from 'react';

import { TransformControls } from '@react-three/drei';
import { ThreeEvent, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';

import { useAppDispatch, useAppSelector } from '../../../hooks.ts';
import { useStore } from '../../../pages/workspace/hooks.ts';
import { updateLampRotationRadians } from '../../../store/reducers/workspace.reducer.ts';
import { getUser } from '../../../store/selectors/user.selector.ts';
import type { LampWireframeMapEntry } from '../../../store/selectors/workspace.selector.ts';
import type { Lamp as LampType } from '../../../types/lamp.ts';
import { isCalculationStarted as checkIsCalculationStarted } from '../../../utils/jobStatus';
import { DistanceLine } from './DistanceLine';
import { MeasurementLine } from './MeasurementLine.tsx';

type LampProps = {
  position: [number, number, number];
  id: string;
  name: string;
  isSelected: boolean;
  showBulb?: boolean;
  showIESModel?: boolean;
  wireframe?: LampWireframeMapEntry;
};

type Point3D = [number, number, number];

type LampDistancePreview = {
  targetLampId: string;
  from: Point3D;
  to: Point3D;
  distance: number;
};

const getLampReferencePoint = (lampPosition: Point3D, lampDistanceToGround: number): Point3D => {
  const normalizedDistanceToGround = lampDistanceToGround > 0 ? lampDistanceToGround : 0;

  // Reuse the same lamp-distance tool reference point:
  // ground under lamp = lamp Z - lamp height above ground.
  return [lampPosition[0], lampPosition[1], lampPosition[2] - normalizedDistanceToGround];
};

const calculateNearestLampDistances = (
  movingLampId: string,
  movingLampPosition: Point3D,
  movingLampDistanceToGround: number,
  allLamps: LampType[],
  count = 3,
): LampDistancePreview[] => {
  if (count <= 0) return [];

  const from = getLampReferencePoint(movingLampPosition, movingLampDistanceToGround);
  const nearest: LampDistancePreview[] = [];

  for (const lamp of allLamps) {
    if (lamp.id === movingLampId) continue;

    const to = getLampReferencePoint(lamp.position, lamp.distanceToGround ?? 0);
    const dx = to[0] - from[0];
    const dy = to[1] - from[1];
    const dz = to[2] - from[2];
    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

    const candidate: LampDistancePreview = {
      targetLampId: lamp.id,
      from,
      to,
      distance,
    };

    // Keep only the N closest candidates with insertion into a tiny sorted buffer.
    if (nearest.length === 0) {
      nearest.push(candidate);
      continue;
    }

    let insertAt = nearest.length;
    for (let i = 0; i < nearest.length; i += 1) {
      if (distance < nearest[i].distance) {
        insertAt = i;
        break;
      }
    }

    if (insertAt < count) {
      nearest.splice(insertAt, 0, candidate);
      if (nearest.length > count) nearest.pop();
      continue;
    }

    if (nearest.length < count) {
      nearest.push(candidate);
    }
  }

  return nearest;
};

export const Lamp = ({
  position,
  id,
  name,
  isSelected,
  showBulb = true,
  showIESModel = false,
  wireframe,
}: LampProps) => {
  const {
    setSelectedObject,
    hoveredLayer,
    updateLampPosition,
    updateLampDistance,
    selectLamp,
    lamps,
    activeVersion,
    isMeasurementMode,
    showMeasurements,
    isLampDistanceMode,
    lampDistanceSelectedLamps,
    addLampDistanceSelection,
    clearLampDistanceSelection,
    showNearestLampDistancesWhileMoving,
  } = useStore();
  const dispatch = useAppDispatch();
  const groupRef = useRef<THREE.Group>(null);
  const rotationProxyRef = useRef<THREE.Group>(null);
  const [isRotating, setIsRotating] = useState(false);
  const [hovered, setHovered] = useState(false);
  const dragStartPosition = useRef<[number, number, number] | null>(null);
  const [distanceToGround, setDistanceToGround] = useState<number | null>(null);
  const [intersectionPoint, setIntersectionPoint] = useState<THREE.Vector3 | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [nearestLampPreviews, setNearestLampPreviews] = useState<LampDistancePreview[]>([]);
  const [wireframeState, setWireframeState] = useState<{
    object: THREE.Object3D;
    rotation: number;
  } | null>(null);
  const wireframeData = wireframe?.wireframe;
  const isHovered = hoveredLayer === 'lamp' || hovered;
  const { scene, camera, controls } = useThree();
  const user = useAppSelector(getUser)!;
  const unitSystem = user.settings.units;

  // Load IES wireframe model
  useEffect(() => {
    if (!showIESModel || !wireframeData?.url) {
      setWireframeState(null);
      return;
    }

    // Clear immediately when URL changes - prevents showing old wireframe with new rotation while loading
    setWireframeState(null);

    const loader = new OBJLoader();
    loader
      .loadAsync(wireframeData.url)
      .then((obj) => {
        obj.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.material = new THREE.MeshBasicMaterial({
              color: 0xbdb904,
              wireframe: true,
            });
          }
        });
        obj.userData.lampName = name;
        setWireframeState({ object: obj, rotation: wireframeData.rotation ?? 0 });
      })
      .catch((error) => {
        console.error(`Failed to load wireframe ${wireframeData.url}:`, error);
        setWireframeState(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rotation intentionally omitted; second effect handles rotation-only updates
  }, [showIESModel, wireframeData?.url, name]);

  // When rotation changes without URL changing (e.g. same IES, different placement), update rotation
  useEffect(() => {
    const rotation = wireframeData?.rotation ?? 0;
    setWireframeState((prev) => (prev && prev.rotation !== rotation ? { ...prev, rotation } : prev));
  }, [wireframeData?.rotation]);

  const isCalculationStarted = checkIsCalculationStarted(activeVersion);
  const isCalculationNotStarted = !isCalculationStarted;
  const lampObject = lamps.find((lamp) => lamp.id === id);
  const raycaster = useRef(new THREE.Raycaster());

  // A catalogue variant assignment (market-matched versions) makes the luminaire
  // rotatable around the vertical axis via an in-scene gizmo.
  const hasVariant = lampObject?.variantId != null;

  // Keep the (invisible) rotation gizmo proxy in sync with the stored rotation,
  // except while the user is actively dragging the ring.
  useEffect(() => {
    if (rotationProxyRef.current && !isRotating) {
      rotationProxyRef.current.rotation.z = lampObject?.rotationRadians ?? 0;
    }
  }, [lampObject?.rotationRadians, isSelected, isRotating]);

  const handleRotateChange = () => {
    if (!rotationProxyRef.current) return;
    const raw = rotationProxyRef.current.rotation.z;
    const normalized = ((raw % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    dispatch(updateLampRotationRadians({ id, rotationRadians: normalized }));
  };

  // Check if this lamp is selected for distance measurement
  const isSelectedForDistance = lampDistanceSelectedLamps.includes(id);

  useEffect(() => {
    if (camera && raycaster.current) {
      raycaster.current.camera = camera;
    }
  }, [camera]);

  // Pre-allocate reusable vectors to avoid per-frame allocations
  const _worldPos = useRef(new THREE.Vector3());
  const _rayDir = useRef(new THREE.Vector3(0, 0, -1));
  const _rayOrigin = useRef(new THREE.Vector3());
  const _lastPosition = useRef(new THREE.Vector3());
  const _frameCounter = useRef(0);
  const _groundDirty = useRef(true);
  const _sceneMeshCount = useRef(0);
  const _groundIntersectionScratch = useRef(new THREE.Vector3());
  const _displayedHeight = useRef<number | null>(null);
  const _displayedIntersection = useRef<THREE.Vector3 | null>(null);

  const collectValidMeshes = (): THREE.Mesh[] => {
    const meshes: THREE.Mesh[] = [];
    scene.traverse((object: THREE.Object3D) => {
      if (object instanceof THREE.Mesh && object.userData.layerName) {
        meshes.push(object);
      }
    });
    return meshes;
  };

  const syncGroundDisplay = (height: number | null, intersection: THREE.Vector3 | null | undefined): void => {
    if (height == null || height <= 0) {
      if (_displayedHeight.current !== null) {
        _displayedHeight.current = null;
        setDistanceToGround(null);
      }
      if (intersection !== undefined && _displayedIntersection.current !== null) {
        _displayedIntersection.current = null;
        setIntersectionPoint(null);
      }
      return;
    }

    if (_displayedHeight.current !== height) {
      _displayedHeight.current = height;
      setDistanceToGround(height);
    }

    if (intersection === undefined) return;

    if (intersection === null) {
      if (_displayedIntersection.current !== null) {
        _displayedIntersection.current = null;
        setIntersectionPoint(null);
      }
      return;
    }

    const prev = _displayedIntersection.current;
    if (prev && prev.x === intersection.x && prev.y === intersection.y && prev.z === intersection.z) {
      return;
    }
    const next = intersection.clone();
    _displayedIntersection.current = next;
    setIntersectionPoint(next);
  };

  // Mark ground distance as needing recomputation when position changes
  useEffect(() => {
    _groundDirty.current = true;
  }, [position]);

  // Meshes are re-loaded on version switch; reset so the next raycast re-scans the scene.
  useEffect(() => {
    _sceneMeshCount.current = 0;
    _groundDirty.current = true;
  }, [activeVersion?.id]);

  useFrame(() => {
    if (!groupRef.current) return;
    // In the measurement view the illuminated model meshes aren't in the scene
    // (only MeasurementColoredModel is), so the downward ground raycast would
    // miss and clobber the stored height with -1 (N/A). Use the stored distance
    // instead — the height was already computed while editing the scene.
    if (isCalculationNotStarted && !showMeasurements) {
      _frameCounter.current += 1;

      groupRef.current.getWorldPosition(_worldPos.current);

      // Only re-raycast when the lamp moved or on a periodic check (every 30 frames)
      const positionChanged = !_worldPos.current.equals(_lastPosition.current);
      if (positionChanged) {
        _lastPosition.current.copy(_worldPos.current);
        _groundDirty.current = true;
      }

      if (!_groundDirty.current && _frameCounter.current % 30 !== 0) return;

      const validMeshes = collectValidMeshes();
      if (validMeshes.length !== _sceneMeshCount.current) {
        _sceneMeshCount.current = validMeshes.length;
        _groundDirty.current = true;
      }

      _groundDirty.current = false;

      // Model meshes mount asynchronously after a version switch (common for
      // market-matched versions). Don't mark N/A until geometry is in the scene.
      if (validMeshes.length === 0) {
        const storedHeight = lampObject?.distanceToGround;
        if (storedHeight != null && storedHeight > 0) {
          syncGroundDisplay(storedHeight, undefined);
        }
        return;
      }

      _rayOrigin.current.copy(_worldPos.current);
      raycaster.current.set(_rayOrigin.current, _rayDir.current);
      let intersects: THREE.Intersection[] = [];
      try {
        intersects = raycaster.current.intersectObjects(validMeshes, true);
      } catch (error) {
        console.error('Lamp ground raycast failed:', error);
        return;
      }
      if (intersects.length > 0) {
        const distance = _worldPos.current.z - intersects[0].point.z;
        if (distance > 0) {
          syncGroundDisplay(distance, intersects[0].point);
          if (
            lampObject &&
            (lampObject.distanceToGround == null || Math.abs(lampObject.distanceToGround - distance) > 0.01)
          ) {
            updateLampDistance(id, distance);
          }
        }
      } else {
        const storedHeight = lampObject?.distanceToGround;
        // Only mark N/A when the lamp moved or has no valid persisted height.
        // Avoid clobbering API heights while the scene is still settling.
        if (positionChanged || storedHeight == null || storedHeight <= 0) {
          syncGroundDisplay(null, null);
          if (lampObject && storedHeight !== -1) {
            updateLampDistance(id, -1);
          }
        } else {
          const groundPoint = _groundIntersectionScratch.current.set(
            _worldPos.current.x,
            _worldPos.current.y,
            _worldPos.current.z - storedHeight,
          );
          syncGroundDisplay(storedHeight, groundPoint);
        }
      }
    } else {
      if (lampObject && lampObject.distanceToGround !== undefined) {
        const storedDistance = lampObject.distanceToGround;
        if (storedDistance > 0) {
          groupRef.current.getWorldPosition(_worldPos.current);
          const groundPoint = _groundIntersectionScratch.current.set(
            _worldPos.current.x,
            _worldPos.current.y,
            _worldPos.current.z - storedDistance,
          );
          syncGroundDisplay(storedDistance, groundPoint);
        } else {
          syncGroundDisplay(null, null);
        }
      }
    }
  });

  const handleClick = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();

    // Mark the native event so Scene's native click handler skips it
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e.nativeEvent as any).__lampHandled = true;

    // Handle lamp distance mode
    if (isLampDistanceMode) {
      // Get the lamp's distanceToGround from the lamps array
      const lampObject = lamps.find((l) => l.id === id);
      const lampHeight = lampObject?.distanceToGround ?? 0;
      const referencePoint = getLampReferencePoint(position, lampHeight);

      // SCENARIO 1: Measurement complete (already 2 lamps) -> Start NEW measurement
      if (lampDistanceSelectedLamps.length >= 2) {
        clearLampDistanceSelection(); // Reset everything
        addLampDistanceSelection(id, referencePoint); // Set this lamp as the new 1st point
      }
      // SCENARIO 2: Building measurement (0 or 1 lamp) -> Add point if not already selected
      else if (!lampDistanceSelectedLamps.includes(id)) {
        addLampDistanceSelection(id, referencePoint);
      }

      return;
    }

    // Lock lamp interaction when measurement mode is active
    if (isMeasurementMode) return;

    // Standard selection logic
    if (isSelected) {
      selectLamp('');
      setSelectedObject(null);
    } else {
      selectLamp(id);
      setSelectedObject(groupRef.current?.uuid);
    }
  };

  const handleTransformChange = () => {
    if (groupRef.current) {
      const worldPosition = new THREE.Vector3();
      groupRef.current.getWorldPosition(worldPosition);
      const movingLampPosition: Point3D = [worldPosition.x, worldPosition.y, worldPosition.z];

      updateLampPosition(id, movingLampPosition);

      if (isDragging && showNearestLampDistancesWhileMoving) {
        const movingLampDistance = distanceToGround ?? lampObject?.distanceToGround ?? 0;
        setNearestLampPreviews(calculateNearestLampDistances(id, movingLampPosition, movingLampDistance, lamps, 3));
      }
    }
  };

  return (
    <>
      <group
        ref={groupRef}
        position={position}
        onClick={handleClick}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
        userData={{ type: 'lamp', id, name }}
      >
        {showBulb && (
          <>
            <mesh>
              {/* Bulb Glass */}
              <sphereGeometry args={[0.4, 32, 32]} />
              <meshStandardMaterial
                color={isSelectedForDistance ? '#A3E635' : isSelected ? '#33ccff' : !isHovered ? '#ffaa00' : '#ffee55'}
                emissive={
                  isSelectedForDistance ? '#A3E635' : isSelected ? '#33ccff' : !isHovered ? '#ffaa00' : '#ffee55'
                }
                emissiveIntensity={isSelectedForDistance ? 1.2 : isSelected ? 1.0 : isHovered ? 0.8 : 0.5}
                transparent
                opacity={0.7}
                roughness={0.1}
                metalness={0.1}
              />
            </mesh>

            {/* Bulb Neck */}
            <mesh position={[0, 0, 0.3]} rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.2, 0.25, 0.4, 32]} />
              <meshStandardMaterial
                color={isSelectedForDistance ? '#A3E635' : isSelected ? '#33ccff' : !isHovered ? '#ffaa00' : '#ffee55'}
                emissive={
                  isSelectedForDistance ? '#A3E635' : isSelected ? '#33ccff' : !isHovered ? '#ffaa00' : '#ffee55'
                }
                emissiveIntensity={isSelectedForDistance ? 0.8 : isSelected ? 0.5 : isHovered ? 0.4 : 0.2}
                transparent
                opacity={0.8}
              />
            </mesh>

            {/* Bulb Base (Socket) */}
            <mesh position={[0, 0, 0.6]} rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.2, 0.2, 0.25, 32]} />
              <meshStandardMaterial color="#888888" metalness={0.8} roughness={0.2} />
            </mesh>
          </>
        )}
        {/* IES Wireframe Model */}
        {showIESModel && wireframeState && (
          <group rotation={[0, 0, wireframeState.rotation]}>
            <primitive object={wireframeState.object} />
          </group>
        )}
        {/* Invisible target for the in-scene rotation gizmo (Z axis). */}
        <group ref={rotationProxyRef} />
        {/* Invisible pick target when visuals are hidden (e.g. render/measurement view without IES). */}
        {!showBulb && !(showIESModel && wireframeState) && (
          <mesh>
            <sphereGeometry args={[0.4, 16, 16]} />
            <meshBasicMaterial transparent opacity={0} depthWrite={false} />
          </mesh>
        )}
      </group>
      {intersectionPoint &&
        distanceToGround &&
        distanceToGround > 0 &&
        !(showIESModel && (wireframe?.wireframeLoading || (wireframeData && !wireframeState))) && (
          <DistanceLine
            start={new THREE.Vector3().copy({ x: position[0], y: position[1], z: position[2] })}
            isSelected={isSelected}
            end={intersectionPoint}
            distance={distanceToGround}
            unitSystem={unitSystem}
          />
        )}
      {showNearestLampDistancesWhileMoving &&
        isDragging &&
        nearestLampPreviews.map((preview) => (
          <MeasurementLine
            key={`nearest-${id}-${preview.targetLampId}`}
            points={[preview.from, preview.to]}
            distance={preview.distance}
            segmentDistances={[preview.distance]}
            hoverPoint={null}
            isHoverSnapped={false}
            showCtrlHint={false}
            labelVariant="compact"
            unitSystem={unitSystem}
          />
        ))}
      {isSelected && groupRef.current && isCalculationNotStarted && !isMeasurementMode && !isLampDistanceMode && (
        <TransformControls
          object={groupRef.current}
          mode="translate"
          size={0.8}
          showX={true}
          showY={true}
          showZ={true}
          onObjectChange={handleTransformChange}
          onMouseUp={() => {
            // Re-enable camera controls after transform drag ends
            if (controls) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (controls as any).enabled = true;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).__transformDragging = false;
            setIsDragging(false);
            setNearestLampPreviews([]);
            // Record the move in history when drag ends
            if (groupRef.current && dragStartPosition.current) {
              const worldPosition = new THREE.Vector3();
              groupRef.current.getWorldPosition(worldPosition);
              const newPosition: [number, number, number] = [worldPosition.x, worldPosition.y, worldPosition.z];
              if (
                dragStartPosition.current[0] !== newPosition[0] ||
                dragStartPosition.current[1] !== newPosition[1] ||
                dragStartPosition.current[2] !== newPosition[2]
              ) {
                updateLampPosition(id, newPosition, dragStartPosition.current);
              }
              dragStartPosition.current = null;
            }
          }}
          onMouseDown={() => {
            // Disable all camera controls while dragging transform handles
            if (controls) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (controls as any).enabled = false;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).__transformDragging = true;
            if (groupRef.current) {
              const worldPosition = new THREE.Vector3();
              groupRef.current.getWorldPosition(worldPosition);
              const startPosition: Point3D = [worldPosition.x, worldPosition.y, worldPosition.z];
              dragStartPosition.current = startPosition;
              setIsDragging(true);
              if (showNearestLampDistancesWhileMoving) {
                setNearestLampPreviews(
                  calculateNearestLampDistances(
                    id,
                    startPosition,
                    distanceToGround ?? lampObject?.distanceToGround ?? 0,
                    lamps,
                    3,
                  ),
                );
              }
            }
          }}
        />
      )}
      {/* Rotation gizmo (Z axis) for catalogue-assigned luminaires */}
      {isSelected &&
        hasVariant &&
        showIESModel &&
        rotationProxyRef.current &&
        isCalculationNotStarted &&
        !isMeasurementMode &&
        !isLampDistanceMode && (
          <TransformControls
            object={rotationProxyRef.current}
            mode="rotate"
            size={1.1}
            showX={false}
            showY={false}
            showZ={true}
            onObjectChange={handleRotateChange}
            onMouseDown={() => {
              if (controls) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (controls as any).enabled = false;
              }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (window as any).__transformDragging = true;
              setIsRotating(true);
            }}
            onMouseUp={() => {
              if (controls) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (controls as any).enabled = true;
              }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (window as any).__transformDragging = false;
              handleRotateChange();
              setIsRotating(false);
            }}
          />
        )}
    </>
  );
};
